<template>
  <Modal
    data-cy="acceptPaymentModal"
    :title="`Accept Payment, Invoice #${invoice?.invoice_id}`"
    :saving="saving"
    class-name="acceptPaymentModal"
    saving-text="Processing..."
    submit-btn-text="Pay"
    @hide-modal="$emit('hide-modal')"
    @submit="handleApply"
  >
    <div v-if="billingCustomer" class="accept-payment">
      <div class="flex flex-col divide-y gap-4">
        <div>
          <div class="text-title">Bill To</div>
          <div class="section-content d-flex flex-column customer-info">
            <h3 title="customer name" data-cy="customerFullName" class="mb-0">
              {{ billingCustomer.full_name }}
            </h3>
            <div class="flex flex-col md:flex-row items-start">
              <span class="whitespace-nowrap">{{ billingCustomer.phone }}</span>
              <span data-cy="billingAddress">{{ billingAddress }}</span>
            </div>
          </div>
        </div>
        <div class="pt-4">
          <div class="text-title">Payment Info</div>
          <div class="section-content">
            <template v-if="progressBilling">
              <div
                :class="['spb-current-draw-box', spbDrawBoxModifier]"
                data-cy="spbCurrentDrawBox"
              >
                <div class="spb-current-draw-tag">
                  <span
                    v-if="progressBilling.past_due"
                    class="icon fa-regular fa-triangle-exclamation mr-1"
                  ></span>
                  {{ spbDrawTagText }}
                </div>
                <div class="spb-current-draw-row">
                  <span
                    class="spb-current-draw-label"
                    data-cy="spbCurrentDrawLabel"
                    >{{ progressBilling.current_draw.label }}</span
                  >
                  <span
                    class="spb-current-draw-amount"
                    data-cy="spbCurrentDrawAmount"
                    >{{
                      dollars(progressBilling.current_draw.remaining_cents)
                    }}</span
                  >
                </div>
                <div
                  v-if="spbDueDateLine"
                  class="spb-current-draw-due"
                  data-cy="spbCurrentDrawDue"
                >
                  {{ spbDueDateLine }}
                </div>
              </div>
              <div class="spb-summary-box" data-cy="spbSummaryBox">
                <div class="spb-summary-row">
                  <span>Contract Total</span>
                  <span data-cy="spbContractTotal">{{
                    dollars(progressBilling.contract_total_cents)
                  }}</span>
                </div>
                <div class="spb-summary-row">
                  <span>Paid to Date</span>
                  <span data-cy="spbPaidToDate">{{
                    dollars(progressBilling.paid_to_date_cents)
                  }}</span>
                </div>
                <hr class="spb-summary-divider" />
                <div class="spb-summary-row spb-summary-total">
                  <span>Total Balance Remaining</span>
                  <span
                    data-cy="spbTotalBalanceRemaining"
                    class="text-danger"
                    >{{
                      dollars(progressBilling.total_balance_remaining_cents)
                    }}</span
                  >
                </div>
              </div>
              <PaymentScheduleSection
                v-if="jobPaymentSchedule"
                :schedule="jobPaymentSchedule"
                :payment-context="progressBilling"
                compact
                data-cy="spbDrawBreakdown"
              />
              <div
                v-if="hasUnallocatedCredit"
                class="spb-apply-credit-box"
                data-cy="spbApplyCreditBox"
              >
                <label
                  class="spb-apply-credit-label"
                  :title="
                    mustApplyCredit
                      ? 'Unallocated funds must be applied to the final draw'
                      : undefined
                  "
                >
                  <input
                    v-model="applyCredit"
                    type="checkbox"
                    :disabled="mustApplyCredit"
                    data-cy="spbApplyCreditCheckbox"
                  />
                  Apply {{ dollars(progressBilling.applyable_credit_cents) }}
                  unallocated credit to this draw
                </label>
              </div>
              <div class="spb-payment-amount">
                <label class="spb-payment-amount-label">
                  Payment Amount:
                  <span
                    v-if="refreshing"
                    class="spb-refreshing-indicator"
                    data-cy="spbRefreshingIndicator"
                  >
                    <span
                      class="icon fa-regular fa-arrows-rotate fa-spin mr-1"
                    ></span
                    >Refreshing…
                  </span>
                </label>
                <TextField
                  v-model="paymentAmount"
                  name="paymentAmount"
                  data-cy="paymentAmount"
                  currency
                  class="mb-0 spb-payment-amount-field"
                  label="Payment Amount"
                  no-label
                />
              </div>
            </template>
            <div v-else class="d-flex payment-values mt-2 mb-2 gap-1">
              <div class="d-flex">
                <span class="mr-2 nowrap">Invoice Total:</span>
                <span data-cy="invoiceTotal">{{
                  dollars(invoice.financials.total_cents)
                }}</span>
              </div>
              <div class="d-flex">
                <span class="mr-2 nowrap">Balance Remaining:</span>
                <span
                  data-cy="balanceRemaining"
                  :class="[
                    'mr-1',
                    {
                      'text-danger': invoice.financials.balance_cents > 0
                    }
                  ]"
                  >{{ dollars(invoice.financials.balance_cents) }}</span
                >
              </div>
              <div class="d-flex justify-content-end payment-amount-label">
                <span class="mr-2 nowrap">Payment Amount:</span>
                <TextField
                  v-model="paymentAmount"
                  name="paymentAmount"
                  data-cy="paymentAmount"
                  currency
                  style="width: 130px"
                  class="mb-0 payment-amount-field"
                  input-class="text-right"
                  label="Payment Amount"
                  no-label
                />
              </div>
            </div>
            <div
              v-if="v$.paymentAmount.$errors.length"
              class="validation-payment-message d-flex"
            >
              <div class="d-flex align-items-center ml-auto">
                <span
                  class="icon fa-regular fa-triangle-exclamation mr-2"
                ></span>
                <span>{{ v$.paymentAmount.$errors[0]?.$message }}</span>
              </div>
            </div>
            <hr />
            <PaymentMethods
              ref="paymentForm"
              class="pt-4"
              data-cy="paymentMethods"
              :invoice-id="invoice.invoice_id"
              :job-id="invoice.job_id"
              :customer="billingCustomer"
              :payment-amount-cents="paymentAmount"
              :financing-enabled="financingEnabled"
            />
          </div>
        </div>
      </div>
    </div>
    <template v-if="disabled" #submit>
      <div></div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { mapActions, mapGetters } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { trackEvent } from "@/lib/pendo";

import PaymentScheduleSection from "@/serviceshift-ui/components/QuoteInvoice/PaymentScheduleSection.vue";
import { Customer } from "@/typings/customer";
import { Invoice } from "@/typings/invoice";
import { useVuelidate } from "@vuelidate/core";
import { helpers, maxValue, minValue, required } from "@vuelidate/validators";
import Modal from "../components/Modal.vue";
import TextField from "../components/TextField.vue";
import PaymentMethods from "./legacy/PaymentMethods.vue";
import usePaymentMethods from "./usePaymentMethods";

export default {
  name: "AcceptPayment",
  components: {
    Modal,
    PaymentMethods,
    PaymentScheduleSection,
    TextField
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object as () => Invoice,
      default: null
    }
  },
  emits: ["payment-success", "payment-method-upated", "hide-modal"],
  setup() {
    const { paymentAmountErrorMessage, fetchPaymentReceivers } =
      usePaymentMethods();
    return {
      paymentAmountErrorMessage,
      fetchPaymentReceivers,
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      paymentAmount: {
        required,
        minValue: minValue(0.01),
        maxValue: helpers.withMessage(
          "Cannot charge more than the balance due",
          maxValue(this.invoice?.financials.balance_cents)
        )
      }
    };
  },
  data() {
    return {
      hasPaymentAmountError: false,
      paymentAmount: 0,
      saving: false,
      refreshing: false,
      jobPaymentSchedule: null,
      applyCredit: false,
      amountInitialized: false
    };
  },
  watch: {
    invoice: {
      immediate: true,
      deep: true,
      handler(newInvoice) {
        if (!newInvoice) return;
        if (this.amountInitialized) return;
        this.amountInitialized = true;
        const spb = newInvoice.progress_billing;
        // AC11: when the plan is cancelled, drop back to the standard
        // payment pre-fill — current_draw no longer applies.
        const planActive =
          spb?.is_progress_billing && !spb.cancelled && spb.current_draw;
        // On the last draw, unallocated credit must be applied — the user
        // cannot leave funds floating when there are no further draws.
        // Gate on `applyable_credit_cents` (the NULL-FK portion the
        // controller can actually reassign), not `unallocated_credit_cents`
        // — see ProgressBillingPayload doc.
        const lastDraw = planActive && spb.next_draw_after_current == null;
        const hasCredit = (spb?.applyable_credit_cents ?? 0) > 0;
        const forceCredit = lastDraw && hasCredit;
        this.applyCredit = forceCredit;
        if (planActive) {
          const remaining = spb.current_draw.remaining_cents;
          this.paymentAmount = forceCredit
            ? Math.max(remaining - spb.applyable_credit_cents, 1)
            : remaining;
        } else {
          this.paymentAmount = newInvoice.financials.balance_cents;
        }
      }
    },
    applyCredit(checked) {
      if (!this.progressBilling) return;
      const drawRemaining = this.progressBilling.current_draw.remaining_cents;
      if (checked) {
        const credit = this.progressBilling.applyable_credit_cents;
        this.paymentAmount = Math.max(drawRemaining - credit, 1);
      } else {
        this.paymentAmount = drawRemaining;
      }
    }
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant", "financingEnabled"]),
    progressBilling({ invoice }) {
      const spb = invoice?.progress_billing;
      // AC11: a cancelled plan suppresses the SPB-aware modal sections
      // (Current Draw box, Summary box, pre-filled draw amount). The
      // table on the invoice screen stays visible in view-only mode,
      // but the payment modal collects against the invoice balance.
      if (spb?.cancelled) return null;
      return spb?.is_progress_billing && spb.current_draw ? spb : null;
    },
    hasUnallocatedCredit({ progressBilling }) {
      // Gate on the NULL-FK portion only — the part the backend can
      // actually reassign. A card/ACH overpayment contributes to
      // `unallocated_credit_cents` (via overpayment_excess) but not to
      // this, so the checkbox stays hidden and can never be a no-op.
      return (progressBilling?.applyable_credit_cents ?? 0) > 0;
    },
    isLastDraw({ progressBilling }) {
      return progressBilling?.next_draw_after_current == null;
    },
    mustApplyCredit() {
      return this.isLastDraw && this.hasUnallocatedCredit;
    },
    spbDrawBoxModifier({ progressBilling }) {
      if (!progressBilling) return "";
      if (progressBilling.past_due) return "spb-current-draw-box--past-due";
      if (progressBilling.current_draw.is_due)
        return "spb-current-draw-box--current";
      return "spb-current-draw-box--no-payment-due";
    },
    spbDrawTagText({ progressBilling }) {
      if (!progressBilling) return "";
      if (progressBilling.past_due) return "PAST DUE DRAW";
      if (progressBilling.current_draw.is_due) return "CURRENT DRAW";
      return "NEXT DRAW";
    },
    spbDueDateLine({ progressBilling }) {
      if (!progressBilling || !progressBilling.current_draw.due_date) return "";
      const formatted = this.formatSpbDueDate(
        progressBilling.current_draw.due_date
      );
      if (progressBilling.past_due) {
        return `Due date: ${formatted} — overdue`;
      }
      if (!progressBilling.current_draw.is_due) {
        return `This draw is not yet due — due ${formatted}`;
      }
      return "";
    },
    billingAddress({ invoice }) {
      if (!invoice.billing_address) {
        return "";
      }
      const { street, city, state, zip_code } = invoice.billing_address;
      return `${street} ${city}, ${state} ${zip_code}`;
    },
    billingCustomer({ invoice }): Customer {
      return invoice?.billing_customer;
    }
  },
  mounted() {
    if (this.invoice) {
      this.fetchPaymentReceivers(this.invoice.payment_receivers_url);
      this.refreshing = true;
      Promise.resolve(this.fetchTransactions(this.invoice)).finally(() => {
        this.refreshing = false;
      });

      if (this.progressBilling) {
        trackEvent("spb_accept_payment_modal_opened", {
          invoice_id: this.invoice.invoice_id,
          payment_status_code: this.progressBilling.payment_status,
          current_draw_label: this.progressBilling.current_draw.label
        });
        this.loadJobPaymentSchedule();
      }
    }
  },
  methods: {
    ...mapActions([
      "fetchTransactions",
      "createTransaction",
      "fetchJobPaymentSchedule",
      "confirmDelete"
    ]),
    async loadJobPaymentSchedule() {
      // PaymentScheduleSection needs the JobPaymentSchedule rows for
      // `value` / `calculated_opposite_value` / `position`; the
      // progress_billing payload only exposes the per-row derived state.
      // If the fetch fails the table is suppressed — the existing draw
      // summary boxes still render so the modal stays useful.
      try {
        const response = await this.fetchJobPaymentSchedule(
          this.invoice.job_id
        );
        this.jobPaymentSchedule = response?.schedule ?? null;
      } catch {
        this.jobPaymentSchedule = null;
      }
    },
    formatSpbDueDate(isoDate: string): string {
      if (!isoDate) return "";
      const [y, m, d] = isoDate.split("-");
      return `${m}/${d}/${y}`;
    },
    async handleApply() {
      if (!this.saving) {
        window.notify.close();
        this.saving = true;
        const valid = await this.v$.$validate();

        if (!valid) {
          this.saving = false;
          return;
        }

        try {
          if (this.progressBilling) {
            if (!this.progressBilling.current_draw.is_due) {
              try {
                await this.confirmDelete({
                  title: "No draw currently due",
                  message:
                    "This invoice does not currently have a draw due. Proceed with payment anyway?"
                });
              } catch {
                this.saving = false;
                return;
              }
            }

            const drawCents = this.progressBilling.current_draw.remaining_cents;
            const creditCents = this.applyCredit
              ? this.progressBilling.applyable_credit_cents
              : 0;
            const effectiveTotal = this.paymentAmount + creditCents;
            if (effectiveTotal > drawCents) {
              try {
                await this.confirmDelete({
                  title: "Payment exceeds current draw",
                  message:
                    "This payment exceeds the current draw. Any overage will be applied to the invoice balance, and can be applied to subsequent draws when they become due."
                });
              } catch {
                this.saving = false;
                return;
              }
            } else if (effectiveTotal < drawCents) {
              try {
                await this.confirmDelete({
                  title: "Partial draw payment",
                  message:
                    "This payment is less than the current draw amount. The balance of the draw will remain due."
                });
              } catch {
                this.saving = false;
                return;
              }
            }
          }

          const formData = await this.$refs.paymentForm.getData();

          const minAmount = 1;
          if (this.paymentAmount < minAmount) {
            window.notify.error(
              `Minimum payment amount of $${minAmount / 100} not met!`
            );
            return;
          }

          const data = {
            amount_cents: this.paymentAmount,
            ...formData,
            ...(this.applyCredit ? { apply_credit: true } : {})
          };

          await this.createTransaction({
            invoice: this.invoice,
            data
          });

          if (
            this.progressBilling &&
            !this.progressBilling.current_draw.is_due
          ) {
            trackEvent("spb_early_payment_recorded", {
              invoice_id: this.invoice.invoice_id,
              current_draw_label: this.progressBilling.current_draw.label,
              amount_cents: this.paymentAmount,
              payment_status_code: this.progressBilling.payment_status
            });
          }

          window.notify.success("Payment processed successfully!");
          this.$emit("payment-success");
          this.$emit("hide-modal");

          // Use the bus since this needs to be handled in grandparent component(s)
          if (
            formData.card_save_payment_method ||
            formData.ach_save_payment_method
          ) {
            EventBus.$emit("payment-method-updated");
          }
        } catch (err) {
          const error = err; // make debuggable
          console.log(
            "Error occurred on payment (should have already notified internally): "
          );
          console.log(error.message || error);
        } finally {
          this.saving = false;
        }
      }
    },
    async validate() {
      return await this.v$.$validate();
    }
  }
};
</script>

<style lang="scss">
.acceptPaymentModal {
  .validation-payment-message {
    margin-left: auto;
    color: var(--danger);
    .icon {
      font-size: 24px;
    }
  }
  .modal-footer {
    padding-top: 1em;
  }
  .modal-dialog {
    max-width: 800px !important;
  }

  .payment-values {
    font-weight: bold;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .payment-amount-field {
    width: 120px !important;
    margin-top: -5px;
  }

  .spb-current-draw-box {
    border-radius: 8px;
    border: 1px solid transparent;
    padding: 12px 16px;
    margin-bottom: 12px;
    margin-top: 10px;

    &--current {
      background: #fffbeb;
      border-color: #fcd34d;
      .spb-current-draw-tag {
        color: #b45309;
      }
    }

    &--past-due {
      background: #fef2f2;
      border-color: #fca5a5;
      .spb-current-draw-tag,
      .spb-current-draw-due {
        color: #b91c1c;
      }
    }

    &--no-payment-due {
      background: #f3f4f6;
      border-color: #d1d5db;
      .spb-current-draw-tag,
      .spb-current-draw-due {
        color: #6b7280;
      }
    }

    .spb-current-draw-tag {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.06em;
      margin-bottom: 4px;
    }

    .spb-current-draw-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .spb-current-draw-label {
      font-size: 16px;
      font-weight: 600;
    }

    .spb-current-draw-amount {
      font-size: 22px;
      font-weight: 700;
    }

    .spb-current-draw-due {
      margin-top: 4px;
      font-size: 13px;
    }
  }

  .spb-summary-box {
    background: #f3f4f6;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 12px;

    .spb-summary-row {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;

      &.spb-summary-total {
        font-weight: 700;
      }
    }

    .spb-summary-divider {
      margin: 6px 0;
      border-color: #d1d5db;
    }
  }

  .spb-apply-credit-box {
    background: #eff6ff;
    border: 1px solid #93c5fd;
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 12px;

    .spb-apply-credit-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
      margin: 0;
      cursor: pointer;

      input:disabled {
        opacity: 0.45;
        cursor: default;
      }
    }
  }

  .spb-payment-amount {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;

    .spb-payment-amount-label {
      font-weight: 700;
      font-size: 14px;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .spb-refreshing-indicator {
      font-weight: 500;
      font-size: 12px;
      color: #6b7280;
      font-style: italic;
    }

    .spb-payment-amount-field {
      width: 100%;
      input {
        width: 100%;
        text-align: left;
      }
    }
  }

  .nowrap {
    white-space: nowrap;
  }

  @media (max-width: 800px) {
    .modal-dialog {
      margin-right: 10px;
      margin-left: 10px;
    }
  }

  @media (max-width: 705px) {
    .payment-values {
      flex-direction: column !important;
    }
    .payment-amount-label {
      justify-content: start !important;
    }
    .payment-amount-field {
      margin-top: 0px;
    }
  }
}
</style>
